// ============================================================
// DomainChipInput.js
// Chip-style multi-domain input used by FederationTab's create
// form (W3.5 commit 3/3). Each typed-and-confirmed domain becomes
// a removable chip; the canonical value is the `value` array
// passed up via onChange.
//
// Keyboard contract:
//   Enter         — commit current input as a chip (if valid)
//   Comma / Space — same as Enter (so paste-and-go is natural)
//   Backspace     — when input is empty, remove the last chip
//
// Validation runs at commit time. Invalid format and duplicate
// (case-insensitive) entries surface as inline errors; the chip
// is NOT added.
//
// Note: lowercases on commit. The parent form's submit step does
// not need to re-normalise.
// ============================================================

import React, { useId, useState } from 'react';
import { X } from 'lucide-react';
import './DomainChipInput.css';

export default function DomainChipInput({
  value,
  onChange,
  validate,
  placeholder,
  helperText,
  ariaLabel,
}) {
  const inputId = useId();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);

  const chips = Array.isArray(value) ? value : [];

  function commitChip() {
    const raw = draft.trim();
    if (!raw) return;
    const lower = raw.toLowerCase();

    const validateFn = typeof validate === 'function'
      ? validate
      : () => null;
    const validationError = validateFn(lower);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Case-insensitive duplicate detection.
    if (chips.some((c) => c.toLowerCase() === lower)) {
      setError('Already added');
      return;
    }

    onChange([...chips, lower]);
    setDraft('');
    setError(null);
  }

  function removeChip(index) {
    const next = chips.slice();
    next.splice(index, 1);
    onChange(next);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      // Enter / comma / space → commit.
      e.preventDefault();
      commitChip();
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && chips.length > 0) {
      e.preventDefault();
      removeChip(chips.length - 1);
    }
  }

  function handleBlur() {
    // Commit on blur for paste-then-tab-away workflow. Defensive
    // against double-commit on Enter→blur sequence: commitChip
    // is a no-op when draft is empty.
    if (draft.trim().length > 0) commitChip();
  }

  return (
    <div className="domain-chip-input">
      <div
        className={`domain-chip-input__field ${error ? 'domain-chip-input__field--error' : ''}`}
        onClick={() => document.getElementById(inputId)?.focus()}
      >
        {chips.map((chip, i) => (
          <span key={`${chip}-${i}`} className="domain-chip-input__chip">
            <span className="domain-chip-input__chip-text">{chip}</span>
            <button
              type="button"
              className="domain-chip-input__chip-remove"
              onClick={(e) => { e.stopPropagation(); removeChip(i); }}
              aria-label={`Remove ${chip}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          className="domain-chip-input__input"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={chips.length === 0 ? (placeholder || 'example.co.uk') : ''}
          autoComplete="off"
          aria-label={ariaLabel || 'Domain'}
        />
      </div>
      {error && (
        <p className="domain-chip-input__error" role="alert">{error}</p>
      )}
      {!error && helperText && (
        <p className="domain-chip-input__help">{helperText}</p>
      )}
    </div>
  );
}
